<template>
  <div
    class="sidebar-form-group"
    data-test="graphics-backgroundImageSettings"
  >
    <label class="checkbox header-checkbox">Background image
      <input
        v-model="backgroundImage"
        type="checkbox"
        data-test="graphics-backgroundImageSettings-toggleBackgroundImage"
      >
      <div class="checkmark">
        <span class="checkmark-tick fa-solid fa-check" />
      </div>
    </label>

    <!-- Data Mapping Controls -->
    <div
      v-if="showDataMapControls && backgroundImage"
      class="margin-top-md"
    >
      <p
        v-if="checkMapped('backgroundImageUrl')"
        class="sidebar-form-label"
        data-test="graphics-backgroundImageSettings-mapData"
      >
        From Data Provider
      </p>
      <map-data-control
        :data-provider="dataProvider"
        :element="element"
        :scroll-offset="scrollOffset"
        :mapped="checkMapped('backgroundImageUrl')"
        property="backgroundImageUrl"
        @update-map="mapToField"
        @clear-map="clearMapping"
      />
    </div>

    <!-- Controls for uploading image through asset manager -->
    <div v-if="backgroundImage" class="margin-top-md">
      <label v-if="dataProvider" class="sidebar-form-label">
        {{ checkMapped('backgroundImageUrl') ? 'Fallback image' : 'Static image' }}
      </label>

      <asset-changer
        :preview-image="backgroundImageUrl"
        asset-type="image"
        allow-remove=""
        @modal="$emit('modal', $event)"
        @select-asset="changeBackgroundImage"
        @remove-asset="removeBackgroundImage"
      />

      <div v-if="backgroundImageUrl || checkMapped('backgroundImageUrl')" class="margin-top-sm">
        <label class="radio inline-radio" for="cover"
          >Fill image
          <input
            id="cover"
            v-model="backgroundSize"
            type="radio"
            value="cover"
            data-test="graphics-backgroundImageSettings-fillImage"
            data-entity="graphics-backgroundImageSettings-fillImage"
          />
          <div class="checkmark"></div>
        </label>
        <label class="radio inline-radio" for="contain"
          >Fit image
          <input
            id="contain"
            v-model="backgroundSize"
            type="radio"
            value="contain"
            data-test="graphics-backgroundImageSettings-fitImage"
            data-entity="graphics-backgroundImageSettings-fitImage"
          />
          <div class="checkmark"></div>
        </label>
      </div>

      <div
        v-if="backgroundImageUrl && backgroundSize === 'cover'"
        class="sidebar-sub-group position"
      >
        <label class="sidebar-form-label">Position</label>

        <div class="mask-position-controls">
          <button
            v-for="p in maskPositions"
            :key="p"
            class="mask-position-controls__control"
            :class="{
              'center': p === 'center center',
              'selected': backgroundPosition === p,
            }"
            :data-test="`graphics-backgroundImageSettings-setPosition-${p}`"
            :data-entity="`graphics-backgroundImageSettings-setPosition-${p}`"
            @click="backgroundPosition = p"
          >
            <span class="sr-only">{{ p }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SettingsMixins from '../../mixins/settings-mixins';
import MapDataControl from './Data/MapDataControl.vue';
import AssetChanger from './AssetChanger.vue';

export default {
  emits: ['modal'],
  name: 'BackgroundImageSettings',
  components: {
    MapDataControl,
    AssetChanger,
  },
  mixins: [SettingsMixins],
  props: {
    element: { required: true },
    scrollOffset: { required: true },
  },
  data() {
    return {
      maskPositions: [
        'top left',
        'top center',
        'top right',
        'center left',
        'center center',
        'center right',
        'bottom left',
        'bottom center',
        'bottom right',
      ],
    };
  },
  computed: {
    backgroundImage: {
      get() {
        return this.getPropertyValue('backgroundImage');
      },
      set(v) {
        this.updateOneElementValue('backgroundImage', v);
      },
    },
    backgroundImageUrl: {
      get() {
        return this.getPropertyValue('backgroundImageUrl', { mappedPath: 'backgroundImageUrl', emptyValue: '' });
      },
      set(v) {
        this.updateOneElementValue('backgroundImageUrl', v);
      },
    },
    backgroundSize: {
      get() {
        return this.getPropertyValue('backgroundSize');
      },
      set(v) {
        this.updateOneElementValue('backgroundSize', v);
      },
    },
    backgroundPosition: {
      get() {
        return this.getPropertyValue('backgroundPosition', { fallbackValue: 'center center' });
      },
      set(v) {
        this.updateOneElementValue('backgroundPosition', v);
      },
    },
    dataColumns() {
      if (!this.dataProvider) {
        return [];
      }

      const data = this.$store.state[this.dataProvider.id].sheetsData;
      if (!data || !data.length) {
        return [];
      }

      // TODO: This will need to be updated if we support row indices
      const cols = Math.max(...data.map((r) => r.length));

      const getColumnName = (val) =>
        val ? val.text || val.img || '(Blank)' : '(Blank)';

      // Just use the first row to signify columns
      return Array.from({ length: cols }).map((v, i) => getColumnName(data[0][i]));
    },
  },

  methods: {
    removeBackgroundImage() {
      this.backgroundImageUrl = '';
      this.updateOneElementValue('backgroundImageMeta', {});
    },

    changeBackgroundImage({ asset, meta: backgroundImageMeta = {} }) {
      this.backgroundImageUrl = asset?.media_url || '';
      this.updateOneElementValue('backgroundImageMeta', backgroundImageMeta);
    },
  },
};
</script>
